import type { ZodSchema } from 'zod';
import type { SxProps } from '@mui/material';
import type { FieldValues, SubmitHandler, DefaultValues } from 'react-hook-form';

import React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider } from 'react-hook-form';

import { Box, Typography, CircularProgress } from '@mui/material';

import { obterMensagensErro } from 'src/auditoria/utils/obter-mensagens-erro';

interface TxFormProps<T extends FieldValues> {
  defaultValues?: DefaultValues<T>;
  onSubmit: (data: T) => Promise<any>;
  children: React.ReactNode;
  sx?: SxProps;
  schema?: ZodSchema<any>;
  methods?: ReturnType<typeof useForm<T>>; // Permite injetar methods externos
}

export function TxForm<T extends FieldValues>({
  defaultValues,
  onSubmit,
  children,
  sx,
  schema,
  methods: externalMethods,
}: TxFormProps<T>) {
  const generatedMethods = useForm<T>({
    defaultValues,
    resolver: schema ? zodResolver(schema) : undefined,
  });

  const methods = externalMethods ?? generatedMethods;

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const [error, setError] = React.useState<string | null>(null);

  const internalSubmit: SubmitHandler<T> = async (data) => {
    setError(null);
    try {
      await onSubmit(data);
    } catch (err: any) {
      const msgErro = obterMensagensErro(err);
      setError(msgErro);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(internalSubmit)} style={{ width: '100%' }}>
        <Box sx={{ position: 'relative', ...sx }}>
          <fieldset disabled={isSubmitting} style={{ border: 'none', padding: 0, margin: 0 }}>
            <Box>
              {children}
              {error && (
                <Typography color="error" sx={{ mt: 1 }}>
                  {error}
                </Typography>
              )}
            </Box>
          </fieldset>

          {isSubmitting && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                bgcolor: 'rgba(255,255,255,0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
              }}
            >
              <CircularProgress />
            </Box>
          )}
        </Box>
      </form>
    </FormProvider>
  );
}
